import { Injectable } from "@angular/core";

export type PopupSection = "actions" | "search" | "list";
export type PopupPlacement = "below" | "above";

// Sections listed top to bottom, for each side of the field the popup can open on.
export interface PopupLayout {
    readonly below: readonly PopupSection[];
    readonly above: readonly PopupSection[];
}

export const MULTISELECT_POPUP_LAYOUT: PopupLayout = {
    below: ["actions", "search", "list"],
    above: ["list", "search", "actions"],
};

export const DROPDOWNLIST_POPUP_LAYOUT: PopupLayout = {
    below: ["search", "list"],
    above: ["list", "search"],
};

export interface DropdownPopupConfig {
    layout: PopupLayout;
    // Tab stops per section, left to right; none while the section isn't rendered. The list has none: its
    // options are driven with the arrow keys.
    stops: Partial<Record<PopupSection, () => readonly (HTMLElement | null | undefined)[]>>;
    field: () => HTMLElement | null | undefined;
}

// Single source for the popup's section order: drives both the rendered order and the Tab order.
@Injectable()
export class DropdownPopupLayout {
    private config?: DropdownPopupConfig;
    private placement: PopupPlacement = "below";

    connect(config: DropdownPopupConfig): void {
        this.config = config;
    }

    // Called once the popup is positioned: picks the layout for the side it opened on and orders the
    // popup's sections from it (see the `popup-layout` mixin).
    attach(popup: HTMLElement): void {
        const field = this.config?.field();
        const popupRect = popup.getBoundingClientRect();
        const above = !!field && popupRect.height > 0 && popupRect.top < field.getBoundingClientRect().top;

        this.placement = above ? "above" : "below";
        popup.dataset["wkPlacement"] = this.placement;
        this.config?.layout[this.placement].forEach((section, index) => popup.style.setProperty(`--wk-popup-order-${section}`, String(index)));
    }

    // Tab or Shift+Tab pressed while the popup is open. Returns true when the popup should close with focus on
    // the field: either Tab reached the field, or it stepped out of the dropdown and the browser's own Tab carries
    // on from the field.
    handleTab(event: KeyboardEvent): boolean {
        const sequence = this.sequence();
        const index = sequence.indexOf(event.target as HTMLElement);
        if (index === -1) return false;

        const next = sequence[index + (event.shiftKey ? -1 : 1)];
        if (!next) return true;

        event.preventDefault();
        if (next === this.config?.field()) return true;

        next.focus({ preventScroll: true });
        return false;
    }

    // The field sits next to the section nearest to it: before the stops below it, after them above it.
    private sequence(): HTMLElement[] {
        const config = this.config;
        if (!config) return [];

        const stops = config.layout[this.placement]
            .flatMap((section) => config.stops[section]?.() ?? [])
            .filter((stop): stop is HTMLElement => !!stop && !stop.matches(":disabled"));

        const field = config.field();
        if (!field) return stops;

        return this.placement === "below" ? [field, ...stops] : [...stops, field];
    }
}
